import originalAxios from 'axios';
import {
  clearRefreshSessionHint,
  markRefreshSessionHint,
} from '@/helpers/authSession';
import config from '@/config';
import { getRequestHeaderParams } from '@/axios-api';
import { useUserStore } from '@/store/userStore';

export const REFRESH_PATH = '/token/refresh/';

// Deliberately a bare axios call: the refresh must never re-enter the
// response interceptor, otherwise its own 401 triggers a blind logout.
let inFlight = null;

let sessionEpoch = 0;

export const discardInFlightRefresh = () => {
  sessionEpoch += 1;
  inFlight = null;
};

const REFRESH_LOCK_NAME = 'auth-token-refresh';
const REFRESH_LOCK_TIMEOUT_MS = 15000;

// The refresh token lives in a shared httpOnly cookie, so two tabs starting a
// refresh at the same moment both send the pre-rotation value and one of them
// loses. Serialising them across tabs is enough: once the winner is done, the
// browser already carries the rotated cookie into the next request.
const withCrossTabLock = task => {
  if (!navigator.locks?.request) return task();

  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    REFRESH_LOCK_TIMEOUT_MS,
  );

  return navigator.locks
    .request(REFRESH_LOCK_NAME, { signal: controller.signal }, task)
    .catch(err => {
      // A stuck sibling tab must not lock this one out of authentication.
      if (err?.name === 'AbortError') return task();
      throw err;
    })
    .finally(() => clearTimeout(timer));
};

export const isRefreshRequest = requestConfig => {
  const url = requestConfig?.url || '';
  return url.includes('token/refresh');
};

export const refreshAccessToken = () => {
  if (inFlight) return inFlight;

  const startedAtEpoch = sessionEpoch;

  const runRefresh = () => originalAxios
    .post(`${config.apiURL}${REFRESH_PATH}`, undefined, {
      withCredentials: true,
      headers: getRequestHeaderParams(),
    })
    .then(res => {
      const accessToken = res?.data?.access_token;
      if (!accessToken) {
        throw new Error('token refresh returned no access_token');
      }
      if (sessionEpoch !== startedAtEpoch) {
        throw new Error('session was cleared while the token refresh was in flight');
      }
      markRefreshSessionHint();
      useUserStore.getState().setAccessToken(accessToken);
      return accessToken;
    })
    .catch(err => {
      // Only an authorization rejection proves the refresh cookie is gone.
      // Dropping the hint on a network error or a 5xx would stop the next
      // load from even trying, while the cookie is still perfectly valid.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearRefreshSessionHint();
      }
      throw err;
    });

  const request = withCrossTabLock(runRefresh).finally(() => {
    if (inFlight === request) inFlight = null;
  });

  inFlight = request;

  return inFlight;
};
